import { memo, useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { Form } from 'antd';
import { uniqBy } from 'es-toolkit';

import { convertDateESTToUTCToUserLocal, convertUserDayTimeToEST } from '@/utilz';
import { setAppLoader, setFilterToggle, setShipmentModal } from '@/redux/common/CommonActions';
import { getShipmentListInProgress, setShipmentFilters } from '@/redux/Savers/SaversActions';
import { getBranchesByCustomerEffect, getTrailersListEffect, getUserTypeEffect } from '@/redux/Savers/SaversEffects';
import { setSelectedShipments, setShipmentList } from '@/redux/shipment/ShipmentActions';
import AlertModal from '@/views/components/Modals/AlertModal';
import { NOTIFICATION_TYPES, showNotification } from '@/views/components/Modals/notification';
import { normalizeGeneratePartnerRequest } from '@/views/screens/Savers/data-provider/generate-partner/normalize';
import { fetchTrailerMovement } from '@/views/screens/Savers/data-provider/get-trailer-movement';
import { normalizeUpdateTrailerRequest } from '@/views/screens/Savers/data-provider/update-trailer/normalize';
import { useGeneratePartner } from '@/views/screens/Savers/hooks/use-generate-partner';
import { useGetSaversCarriers } from '@/views/screens/Savers/hooks/use-get-savers-carriers';
import { useUpdateTrailer } from '@/views/screens/Savers/hooks/use-update-trailer';

import useDropdownState from '../TrailerManagement/hooks/useDropdownState';
import { getShipmentDefaultFilters, SHIPMENT_TYPE } from './constants';
import SaversShipmentForm from './SaversShipmentForm';
import SaversShipmentGridView from './SaversShipmentGridView';

const SaversShipmentContent = () => {
  const [statusModal, setStatusModal] = useState(false);
  const [shipmentValues, setShipmentValues] = useState({
    shipmentkey: '',
    shipmentNo: '',
    status: '',
    trailerNumber: '',
    trailerRequestID: '',
    trailerRequestStatus: '',
  });
  const [trailerNumberOptions, setTrailerNumberOptions] = useState([]);
  const [partnerLocationOptions, setPartnerLocationOptions] = useState([]);
  const [shipmentNo, setShipmentNo] = useState('');
  const [label, setLabel] = useState('');
  const [trafficCoordinatorOptions, setTrafficCoordinatorOptions] = useState([]);
  const [saversMenuType, setSaversMenuType] = useState('');
  const [trailerMovementData, setTrailerMovementData] = useState(null);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertOpen, setAlertOpen] = useState(false);

  const [trailerShipmentForm] = Form.useForm();
  const dispatch = useDispatch();
  const shipmentFilters = useSelector((state) => state.savers.shipmentFilters, shallowEqual);
  const selectedShipments = useSelector((state) => state.shipment.selectedShipments, shallowEqual);
  const shipmentList = useSelector((state) => state.savers.shipmentList);
  const companyId = useSelector((state) => state.auth.selectedCompany.companyId, shallowEqual);
  const dropdownState = useDropdownState(companyId);

  const { data: carrierOptions, isLoading: carrierOptionsLoading } = useGetSaversCarriers({ customerKey: companyId });
  const { mutate: generatePartnerMutation } = useGeneratePartner();
  const { mutate: updateTrailerMutation } = useUpdateTrailer();
  const handleShipmentClick = (shipmentKey) => {
    dispatch(setShipmentModal(shipmentKey));
    dispatch(setFilterToggle(false));
  };
  // biome-ignore lint/correctness/useExhaustiveDependencies: unwanted dependency
  useEffect(() => {
    if (companyId) {
      getPartnerLocations(companyId);
      dispatch(setSelectedShipments([]));
      const initialFilters = getShipmentDefaultFilters();
      initialFilters.CreatedDateFrom = convertUserDayTimeToEST(initialFilters.CreatedDateFrom, 'start');
      initialFilters.CreatedDateTo = convertUserDayTimeToEST(initialFilters.CreatedDateTo, 'end');
      dispatch(
        setShipmentFilters({
          ...initialFilters,
          pageNumber: 1,
        }),
      );
      dispatch(
        setShipmentList({
          data: [],
          isFirstTime: true,
          latedelivery: 0,
          latepickup: 0,
          missingDocument: 0,
          pickupFailed: 0,
          progressing: true,
          totalRecords: 0,
          trackingfailures: 0,
        }),
      );
      getPartnerLocations(companyId);
    }
  }, [companyId, dispatch]);

  const handleLoadMore = () => {
    dispatch(
      setShipmentFilters({
        ...shipmentFilters,
        pageNumber: (shipmentFilters?.pageNumber || 0) + 1,
      }),
    );
    dispatch(getShipmentListInProgress());
  };
  const totalPageCount = shipmentList?.totalPageCount || 0;

  const getTrailerList = async () => {
    try {
      const { data } = await getTrailersListEffect();
      const options = data.reduce((acc, row) => {
        if (row.trailerNumber && row.trailerID && !acc.some((item) => item.value === row.trailerID)) {
          acc.push({ label: row.trailerNumber, value: row.trailerID });
        }
        return acc;
      }, []);
      setTrailerNumberOptions(options);
    } catch (error) {
      showNotification({
        message: error.message,
        type: NOTIFICATION_TYPES.ERROR,
      });
      setTrailerNumberOptions([]);
    }
  };

  const getPartnerLocations = async (companyId) => {
    try {
      if (!companyId || companyId === '0') {
        return;
      }
      const { data } = await getBranchesByCustomerEffect(companyId);

      const options = data.map((row) => ({
        entityCode: row.entityCode,
        label: row.branchDesc,
        value: row.branchKey,
      }));

      setPartnerLocationOptions(options);
    } catch (error) {
      showNotification({
        message: error.message,
        type: NOTIFICATION_TYPES.ERROR,
      });
      setPartnerLocationOptions([]);
    }
  };

  const getTrafficCoordinator = async () => {
    try {
      const { data } = await getUserTypeEffect('CT');
      const uniqueCoordinators = uniqBy(data, ({ userId }) => userId);
      const options = uniqueCoordinators.map((row) => ({
        label: row.fullName,
        value: row.userId,
      }));
      setTrafficCoordinatorOptions(options);
    } catch (error) {
      showNotification({
        message: error.message,
        type: NOTIFICATION_TYPES.ERROR,
      });
      setTrafficCoordinatorOptions([]);
    } finally {
      dispatch(setAppLoader(false));
    }
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: unwanted dependency
  useEffect(() => {
    getTrailerList();
    getTrafficCoordinator();
  }, []);

  const handleStatusUpdate = async (data, type) => {
    try {
      if (type === 'Generate Inbound Transfer') {
        if (data.directFromPartner && !data.isGeneratePayment && data.isLoadedPickUp) {
          setAlertOpen(true);
          setAlertMessage('Please generate payment first');
          return;
        }
      }
      dispatch(setAppLoader(true));
      const { shipmentNo, isLoadedPickUp, isEmptyPickUp, isLoadedDelivered, trailerMovementID } = data;
      const trailerMovementData = await fetchTrailerMovement({ trailerMovementID });
      const shippingDataList = trailerMovementData?.trailerShippings || [];
      const receivingDataList = trailerMovementData?.trailerReceivings || [];

      const trailerShippings = shippingDataList.map((shipping) => ({
        [`${SHIPMENT_TYPE.org}bookOks`]: shipping?.bookOks,
        [`${SHIPMENT_TYPE.org}bookweight`]: shipping?.bookWeight,
        [`${SHIPMENT_TYPE.org}etd`]: shipping?.etd ? convertDateESTToUTCToUserLocal(shipping.etd) : null,
        [`${SHIPMENT_TYPE.org}furn`]: shipping?.furniturePieces,
        [`${SHIPMENT_TYPE.org}miscOks`]: shipping?.miscOKs,
        [`${SHIPMENT_TYPE.org}miscweight`]: shipping?.hardWeight,
        [`${SHIPMENT_TYPE.org}oli`]: shipping?.otherLargeItems,
        [`${SHIPMENT_TYPE.org}softOks`]: shipping?.clothOKs,
        [`${SHIPMENT_TYPE.org}softweight`]: shipping?.softWeight,
        [`${SHIPMENT_TYPE.org}SpotDate`]: shipping?.spotDate ? convertDateESTToUTCToUserLocal(shipping.spotDate) : null,
        [`${SHIPMENT_TYPE.org}ShipLocation`]: trailerMovementData?.originLocation,
      }));

      const trailerReceivings = receivingDataList.map((receiving) => ({
        [`${SHIPMENT_TYPE.dest}bookOks`]: receiving?.bookOks,
        [`${SHIPMENT_TYPE.dest}bookweight`]: receiving?.bookWeight,
        [`${SHIPMENT_TYPE.dest}eta`]: receiving?.eta ? convertDateESTToUTCToUserLocal(receiving.eta) : null,
        [`${SHIPMENT_TYPE.dest}furn`]: receiving?.furniturePieces,
        [`${SHIPMENT_TYPE.dest}miscOks`]: receiving?.miscOKs,
        [`${SHIPMENT_TYPE.dest}miscweight`]: receiving?.hardWeight,
        [`${SHIPMENT_TYPE.dest}oli`]: receiving?.otherLargeItems,
        [`${SHIPMENT_TYPE.dest}softOks`]: receiving?.clothOKs,
        [`${SHIPMENT_TYPE.dest}softweight`]: receiving?.softWeight,
        [`${SHIPMENT_TYPE.dest}ShipLocation`]: trailerMovementData?.destinationLocation,
      }));

      const trailerShipmentData = {
        carrier: trailerMovementData?.carrierkey,
        commodity: trailerMovementData?.productTypeDesc?.trim(),
        directFromPartner: trailerMovementData?.directFromPartner,
        emptyDate: null,
        entryDate: convertDateESTToUTCToUserLocal(trailerMovementData?.entryDate),
        rollDoor: trailerMovementData?.rollDoor,
        rscm: trailerMovementData?.rscm,
        scs: trailerMovementData?.scs,
        shippingCost: trailerMovementData?.rateShopCost,
        TrafficCoord: trailerMovementData?.trafficCoordinatorID,
        trailerNumber: trailerMovementData?.trailerNumber,
        trailerReceivedFlag: trailerMovementData?.trailerReceivedFlag || false,
        trailerReceivings,
        trailerShippings,
        unitRentalCompanyID: trailerMovementData?.unitRentalCompanyID,
        unitSize: trailerMovementData?.unitSize,
      };

      trailerShipmentForm.setFieldsValue(trailerShipmentData);
      setTrailerMovementData(trailerMovementData);
      const trailerType = isLoadedPickUp
        ? 'Pickup Loaded'
        : isEmptyPickUp
          ? 'Pickup Empty'
          : isLoadedDelivered
            ? 'Delivery Loaded'
            : 'Delivery Empty';

      setLabel(trailerType);
      setSaversMenuType(type);
      setShipmentNo(shipmentNo);
      setStatusModal(!statusModal);
      dispatch(setAppLoader(false));
    } catch (error) {
      showNotification({
        message: error.message,
        type: NOTIFICATION_TYPES.ERROR,
      });
      dispatch(setAppLoader(false));
    }
  };

  const handleSubmit = (data, type) => {
    if (saversMenuType === 'Generate Payment') {
      const currentValues = trailerShipmentForm.getFieldsValue();
      const weightFields = [
        `${SHIPMENT_TYPE.org}softweight`,
        `${SHIPMENT_TYPE.org}miscweight`,
        `${SHIPMENT_TYPE.org}bookweight`,
        `${SHIPMENT_TYPE.org}furn`,
        `${SHIPMENT_TYPE.org}oli`,
      ];

      const hasAnyWeight = weightFields.some((field) => {
        const value = currentValues.trailerShippings[0][field];
        return value !== undefined && value !== null && value !== '' && value !== 0;
      });

      if (!hasAnyWeight) {
        setAlertMessage(
          'At least one weight field (Soft Weight, Misc Weight, Book Weight, Furn, or OLI) is required for Generate Payment shipment type.',
        );
        setAlertOpen(true);
        return;
      }
    }

    if (type !== 'Edit') {
      const payload = normalizeGeneratePartnerRequest(data, type, trailerMovementData, companyId, SHIPMENT_TYPE.org);
      dispatch(setAppLoader(true));
      try {
        generatePartnerMutation(payload);
        setStatusModal(false);
      } catch (error) {
        showNotification({
          message: error.message,
          type: NOTIFICATION_TYPES.ERROR,
        });
      } finally {
        dispatch(setAppLoader(false));
      }
    } else {
      const payload = normalizeUpdateTrailerRequest(data, trailerMovementData, SHIPMENT_TYPE.org);
      dispatch(setAppLoader(true));
      try {
        updateTrailerMutation(payload);
        setStatusModal(false);
      } catch (error) {
        showNotification({
          message: error.message,
          type: NOTIFICATION_TYPES.ERROR,
        });
      } finally {
        dispatch(setAppLoader(false));
      }
    }
  };

  const onAlertConfirm = () => {
    setAlertOpen(false);
  };

  return (
    <div className="h-100 pt-15 d-flex flex-direction-column">
      <SaversShipmentGridView
        handleLoadMore={handleLoadMore}
        isLoading={shipmentList.progressing}
        hasMore={shipmentList.progressing ? false : shipmentList?.data.length < totalPageCount}
        shipmentList={shipmentList.data}
        handleShipmentClick={handleShipmentClick}
        selectedShipments={selectedShipments}
        handleStatusUpdate={handleStatusUpdate}
      />
      <SaversShipmentForm
        statusModal={statusModal}
        setStatusModal={setStatusModal}
        shipmentValues={shipmentValues}
        setShipmentValues={setShipmentValues}
        handleSubmit={handleSubmit}
        trailerNumberOptions={trailerNumberOptions}
        partnerLocationOptions={partnerLocationOptions}
        trailerShipmentForm={trailerShipmentForm}
        shipmentNo={shipmentNo}
        label={label}
        trafficCoordinatorOptions={trafficCoordinatorOptions}
        saversMenuType={saversMenuType}
        trailerMovementData={trailerMovementData}
        dropdownState={dropdownState}
        carrierOptions={carrierOptions}
        carrierOptionsLoading={carrierOptionsLoading}
      />
      <AlertModal
        title="Alert"
        open={alertOpen}
        description={alertMessage}
        onOk={onAlertConfirm}
        onClose={onAlertConfirm}
      />
    </div>
  );
};

export default memo(SaversShipmentContent);
